import { ChangeEvent, useId, useMemo, useRef, useState } from "react";
import debounce from "lodash/debounce";
import clsx from "clsx";

import { type GeoEntity, isArrondissementCity, searchEntity } from '@/utils/geo-search';
import { useClickOutside } from "@/hooks/useClickOutside";
import { Tag } from "@/stories/atoms/Tag/Tag";
import { Icon, isIconName } from '@/stories/atoms/Icon/Icon';
import { DropdownPanel } from "@/stories/components/Form/Dropdown/Dropdown";
import { InputProps } from '@/stories/components/Form/Input/Input';
import { FormItem } from "@/stories/components/Form/FormItem/FormItem";

import styles from './locality-filter.module.css'
import inputStyles from '@/stories/components/Form/Input/input.module.css'
import { Slider } from "../Slider/Slider";
import { Loader } from "@/stories/atoms/Loader/Loader";

export type LocalityType = GeoEntity

/** même entité : code INSEE pour une commune, code pour un département ou un arrondissement */
const isSameLocality = (a: GeoEntity, b: GeoEntity) => a.type === b.type
  && (a.type === 'commune' ? a.codeInsee === (b as typeof a).codeInsee : a.code === (b as typeof a).code)

const LOCALITY_TYPE_LABELS: Record<GeoEntity['type'], string> = {
  commune: 'communes',
  departement: 'départements',
  arrondissement: 'arrondissements',
}

const Locality = ({item, selected, handleClick}: {
  item: GeoEntity
  selected?: boolean,
  handleClick: () => void,
}) => {
  return (
    <button type="button" className={styles.locality} onClick={handleClick}>{item.nom}{item.type === 'commune' ? ` - ${item.codePostal}` : item.type === 'departement' ? ` - ${item.code}` : ''} {selected? <Icon icon='check' size={'1x'} className={styles.check}/>: null}</button>
  )
}

type LocationFilterProps = {
  setLocalities: (localities: GeoEntity[]) => void,
  localities?: GeoEntity[],
  defaultLocations?: GeoEntity[],
  localityTypes?: GeoEntity['type'][]
  /** masque Paris, Lyon et Marseille pour ne proposer que leurs arrondissements */
  excludeArrondissementCities?: boolean
  radiusEnabled?: boolean,
  radiusValue?: number | 'all',
  handleRadiusChange?: (e: ChangeEvent) => void,
  multiple?: boolean,
  className?: string,
  label?: string,
  id?: string
} & Omit<InputProps, 'id' | 'label'>

export const LocalitySearch = ({
  localities = [],
  setLocalities,
  defaultLocations,
  localityTypes = ['commune', 'departement'],
  excludeArrondissementCities = false,
  radiusEnabled = false,
  radiusValue,
  handleRadiusChange,
  multiple = false,
  label,
  id,
  placeholder = 'Ville, code postal,...',
  value,
  setValue,
  required,
  inline,
  helpText,
  errorText,
  hideLabel,
  hideOptionnal,
  icon,
  suffix,
  variant,
  className,
  ...props
}: LocationFilterProps) => {
  const {style, ...inputProps} = props
  // dropdown hooks
  const [open, setOpen] = useState<boolean>(false)
  const generatedId = useId()
  const ref =  useRef<HTMLDivElement | null>(null)
  // List hooks
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [isFocus, setIsFocus] = useState(false)
  const [list, setList] = useState<GeoEntity[]>([])
  const [loading, setLoading] = useState(false)
  const [inputValue, setInputValue] = useState('')
  // annonce le remplacement de la sélection quand deux types de localité ne peuvent pas être combinés
  const [replacedMessage, setReplacedMessage] = useState<string | null>(null)

  const closePanel = () => {
    setReplacedMessage(null)
    setList([])
    if (inputRef.current) inputRef.current.value = ''
    setInputValue('')
    setOpen(false)
  }
  useClickOutside(ref, () => closePanel(), open)

  const filterResults = (results: GeoEntity[]) => results.reduce((acc, curr) => {
    // Allow only certain result type
    if (!localityTypes.includes(curr.type)) return acc
    if (excludeArrondissementCities && curr.type === 'commune' && isArrondissementCity(curr.codeInsee)) return acc

    return [
      ...acc,
      ...(
        // exclude selected entities from results list
        localities.some(loc => isSameLocality(loc, curr))
          ? []
          : [curr])
    ]
  }, [] as GeoEntity[])

  // Créée une seule fois : le filtrage est passé à l'appel pour utiliser les props courantes
  const debouncedSearch = useMemo(() =>
    debounce(async (criteria: string, filter: (results: GeoEntity[]) => GeoEntity[]) => {
      setLoading(true)
      const results = await searchEntity(criteria)
      setList(filter(results))
      setLoading(false)
    }, 300)
  , [])

  const handleChange = async (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
    debouncedSearch(e.target.value, filterResults)
  }

  const toggleLocality = (value: LocalityType) => {
    setReplacedMessage(null)
    const localityFound = localities?.findIndex(loc => loc && isSameLocality(loc, value))
    if (localityFound !== -1) {
      // remove clicked locality from selected (new array to avoid mutation)
      setLocalities(localities.filter((_, i) => i !== localityFound))
    } else {
      // adding clicked locality — selecting a different type (commune / departement / arrondissement) replaces the previous selection, same type accumulates
      const previous = multiple ? (localities ?? []).filter(loc => loc.type === value.type) : []
      const removed = multiple ? (localities ?? []).filter(loc => loc.type !== value.type) : []
      if (removed.length > 0) {
        setReplacedMessage(`Sélection remplacée : impossible de combiner ${LOCALITY_TYPE_LABELS[removed[0].type]} et ${LOCALITY_TYPE_LABELS[value.type]}.`)
      }
      setLocalities([...previous, value])
      if (inputRef.current) inputRef.current.value = ''
      setInputValue('')
    }
    setList([])
  }

  const formatLocalities = () => {
    return `${localities[0].nom}${localities.length > 1 ? `, +${localities.length -1}` : ''}`
  }

  const displaySlider = () => radiusEnabled && localities.length === 1 && localities[0].type === 'commune'

  return (
    <FormItem
      ref={ref}
      id={id || generatedId}
      className={className}
      label={label}
      inline={inline}
      hideLabel={hideLabel}
      hideOptionnal={hideOptionnal}
      required={required}
      helpText={helpText}
      errorText={errorText}
      style={style}>
      <div className={clsx(inputStyles.wrapper, icon && 'has-icon', suffix && 'has-suffix')}>
        {icon ? <Icon icon={icon} className={inputStyles.icon}/> : null}
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          id={id || generatedId}
          autoComplete="off"
          value={value}
          onChange={handleChange}
          onFocus={() => {
            setIsFocus(true)
            setOpen(true)
          }}
          onBlur={() => setIsFocus(false)}
          className={clsx(inputStyles.input, variant && inputStyles[variant], localities.length > 0 && !isFocus && styles.placeholder)}
          {...inputProps}/>
        {localities.length > 0 && !isFocus && inputValue === '' && (
          <span className={styles.selectedLocalities}>
            <span className={styles.selectedLabel}>{formatLocalities()}</span>
            {displaySlider() && radiusValue && radiusValue !== 'all'
              ? <span className={styles.km}> - {radiusValue/1000} km</span>
              : null}
          </span>
        )}
        {suffix ? isIconName(suffix) ? <Icon icon={suffix} className={styles.suffix}/> : <span className={styles.inputStyles}>{suffix}</span> : null}
      </div>
      <DropdownPanel open={open && (loading || list.length > 0 || localities.length > 0)} id={id || generatedId}>
        <div className={styles.wrapper}>
          {list && list.length > 0 ? (
            <div className={styles.list}>
              {list.map((item: LocalityType, i: number) => {
                return <Locality item={item} handleClick={() => toggleLocality(item)} key={item.nom + i}/>
              })}
            </div>
          ) : (
            <>
              {loading ? (
                <div className={styles.loaderWpr}>
                  <Loader className={styles.loader}/>
                </div>
               ) : localities.length > 0 ? null : <p className={styles.noSuggest}>Aucune suggestion</p>}
            </>
          )}
          {replacedMessage ? <span role="alert" className="sr-only">{replacedMessage}</span> : null}
          {localities.length > 0 ? (
            <div className={styles.selectedList}>
              {localities.map((locality, i) => (
                <Tag key={locality.nom + i} asChild>
                  <button type="button" className={styles.selectedLocality} onClick={() => toggleLocality(locality)}>
                    {locality.nom}<Icon icon="close" aria-hidden="true"/>
                  </button>
                </Tag>
              ))}
            </div>
          ) : null}
          {displaySlider() ? (
            <div className={styles.sliderWpr}>
              <Slider
                value={ radiusValue && radiusValue !== 'all' ? (radiusValue/1000).toString() : '0' }
                onChange={handleRadiusChange}
                label="Dans un rayon de"
                id={"rayon"}
                hideOptionnal
                min={0}
                max={100}
                unit ='km'
                marks={[0, 100]}
                step={5}
                variant="secondary"/>
            </div>
          ) : null}
        </div>
      </DropdownPanel>
    </FormItem>
  )
}